import React, { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { CardSelect } from '~/eds/src/components/CardSelect/CardSelect';
import { CreditBalance } from '~/features/billing/CreditBalance/CreditBalance';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { slices } from '~/redux/slices';
import { Uuid } from '~/types/Uuid';

import { calculateCreditEstimate } from '../../billing/utils';
import { ModelProgress } from '../../types/model';

interface Props {
  progress: ModelProgress;
  modelId: Uuid;
  hasOnlyFiltersChanged: boolean;
  onStopPolling: (stopPolling: boolean) => void;
}
export const PublishOptions = ({
  progress,
  modelId,
  hasOnlyFiltersChanged,
  onStopPolling,
}: Props) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { client } = useCurrentUser();

  const { docsInScope, docsProcessed, docsInScopeProcessing } = progress;

  const selectPublishMode = useSelector(
    slices.xRay.selectors.selectPublishMode,
  );

  const newDocsInScope = calculateCreditEstimate(docsProcessed, docsInScope);

  const unprocessedDocsDescription = intl.formatMessage({
    id: 'PublishOptions.unprocessedDocumentsDescription',
    defaultMessage:
      'Only apply edits to documents in scope that the model has not yet run on.',
    description: 'Description for applying edits to unprocessed documents only',
  });

  const unprocessedDocsLabel = intl.formatMessage({
    id: 'PublishOptions.applyEditsToUnprocessedDocuments',
    defaultMessage: 'Apply edits to unprocessed documents in scope',
    description: 'Label for applying edits to unprocessed documents',
  });

  const publishOptions = useMemo(() => {
    const options = [];

    if (hasOnlyFiltersChanged) {
      options.push({
        ariaLabel: 'new documents in scope option',
        content: (
          <CreditBalance
            enableCreditsAvailable={false}
            clientId={client}
            estimateUnit={{
              modelId,
              isLoadingUnitValue: docsInScopeProcessing,
              unit: 'document-count',
              value: newDocsInScope ?? 0,
            }}
          />
        ),
        contentDirection: 'row' as const,
        description: unprocessedDocsDescription,
        label: unprocessedDocsLabel,
        value: 'new_docs_in_scope' as const,
      });
    } else {
      options.push(
        {
          ariaLabel: 'new documents in scope option',
          content: (
            <CreditBalance
              enableCreditsAvailable={false}
              clientId={client}
              estimateUnit={{
                modelId,
                isLoadingUnitValue: docsInScopeProcessing,
                unit: 'document-count',
                value: newDocsInScope ?? 0,
              }}
            />
          ),
          contentDirection: 'row' as const,
          description: unprocessedDocsDescription,
          label: unprocessedDocsLabel,
          value: 'new_docs_in_scope' as const,
        },
        {
          ariaLabel: 'all documents in scope option',
          content: (
            <CreditBalance
              enableCreditsAvailable={false}
              clientId={client}
              estimateUnit={{
                modelId,
                isLoadingUnitValue: docsInScopeProcessing,
                unit: 'document-count',
                value: docsInScope,
              }}
            />
          ),
          contentDirection: 'row' as const,
          description: intl.formatMessage({
            id: 'PublishOptions.allDocumentsDescription',
            defaultMessage:
              'Apply edits to all documents in scope, including ones that the model has already run on. The new answers will overwrite existing answers found by Custom AI.',
            description:
              'Description for applying edits to all documents in scope',
          }),
          label: intl.formatMessage({
            id: 'PublishOptions.applyEditsToAllDocuments',
            defaultMessage: 'Apply edits to all documents in scope',
            description: 'Label for applying edits to all documents',
          }),
          value: 'all_docs_in_scope' as const,
        },
      );
    }

    return options;
  }, [
    hasOnlyFiltersChanged,
    docsInScope,
    docsProcessed,
    docsInScopeProcessing,
    progress,
  ]);

  useEffect(() => {
    if (!docsInScopeProcessing) {
      onStopPolling(docsInScopeProcessing);
    }
  }, [docsInScopeProcessing]);

  return (
    <CardSelect
      direction="column"
      isMulti={false}
      name="post-publish-options"
      onChange={(updatedValue) => {
        dispatch(
          slices.xRay.actions.setPublishMode(
            updatedValue ?? 'new_docs_in_scope',
          ),
        );
      }}
      options={publishOptions}
      value={selectPublishMode}
    />
  );
};
